import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  ImageList,
  ImageListItem,
  Button,
  Link,
} from '@mui/material';
import { Person, ThumbUp, ThumbDown, ShoppingBag } from '@mui/icons-material';
import { Review } from '../types';
import RatingDisplay from './RatingDisplay';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface ReviewCardProps {
  review: Review;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review }) => {
  const navigate = useNavigate();
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleProductClick = () => {
    navigate(`/products/${review.product_id}/reviews`);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Заголовок с информацией о товаре */}
        <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Button
            onClick={handleProductClick}
            startIcon={<ShoppingBag />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'primary.main',
              p: 0,
              mb: 1,
              '&:hover': {
                background: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            {review.product_name}
          </Button>
        </Box>

        {/* Информация о пользователе и рейтинг */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <Person />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">{review.user_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(review.created_at)}
            </Typography>
          </Box>
          <RatingDisplay rating={review.rating} showLabel={false} />
        </Box>

        <Typography variant="body1" sx={{ mb: 2 }}>
          {review.comment}
        </Typography>

        {review.advantages && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ThumbUp color="success" fontSize="small" />
              <Typography variant="subtitle2" color="success.main">
                Достоинства:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 3 }}>
              {review.advantages}
            </Typography>
          </Box>
        )}

        {review.disadvantages && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ThumbDown color="error" fontSize="small" />
              <Typography variant="subtitle2" color="error.main">
                Недостатки:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 3 }}>
              {review.disadvantages}
            </Typography>
          </Box>
        )}

        {review.photos && review.photos.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Фотографии:
            </Typography>
            <ImageList cols={4} rowHeight={120} gap={8}>
              {review.photos.map((photo) => (
                <ImageListItem key={photo.id}>
                  <img
                    src={photo.thumb_url}
                    alt="Фото к отзыву"
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 4,
                    }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ReviewCard;


